import CustomStore from "devextreme/data/custom_store";
import { useMemo, useRef } from "react";
import { callOperation } from "async-ops";
import { GET_EA_TARGET_VALUE_SOURCES } from "../../pages/external-audit/services/get-ea-target-value-sources";
import { SHOW_LOADER, HIDE_LOADER } from "../mdae-common-reducer";
import { handleTargetValueDataSource } from "../target-value-datasource-utils";

export interface UseTargetValueDatasourceConfig {
  targetType: string;
  targetTypeLookup: any[];
  serviceFromDate: any;
  serviceToDate: any;
  auditGroupId?: number;
  dispatch: any;
  selectedCodeList?: any;
  eaAuditId?: number;
  eaCaseId?: number;
  isManualCase?: boolean;
  cachedDataSource?: any[];
  cacheConsumedCallback?: () => void;
  setLocalDataSource?: (data: any[]) => void;
  codeValueInstance?: any;
  existingEntityId?: number;
  setPropertiesDataType?: string;
  propertiesDataKey?: string;
  dataSourceKey?: string;
  loadOnOpen?: boolean;
  showLoader?: boolean;
}

export const isTargetTypeOfCode = (targetType) => {
  if (
    targetType === "CPTHCPCS" ||
    targetType === "ICDD" ||
    targetType === "ICDP" ||
    targetType === "DRG" ||
    targetType === "APR_DRG" ||
    targetType === "MODIFIER" ||
    targetType === "PROVIDER" ||
    targetType === "FACILITY" ||
    targetType === "RENDERING_PROVIDER_GROUP"
  ) {
    return true;
  }
  return false;
};

export function getSearchMinLength(codeType: string): number {
  const upper = codeType?.toUpperCase();
  if (
    upper === "MODIFIER" ||
    upper === "PLACE_OF_SERVICE" ||
    upper === "DRG_SERVICE_TYPE" ||
    upper === "FACILITY" ||
    upper === "RENDERING_PROVIDER_GROUP"
  ) {
    return 0;
  }
  if (upper === "ICD_DIAGNOSIS" || upper === "ICD_PROCEDURE") {
    return 1;
  }
  return 2;
}

function meetsSearchThreshold(
  codeType: string,
  searchValue: string | undefined
): boolean {
  if (!searchValue) return false;
  const minLength = getSearchMinLength(codeType);
  return searchValue.length > minLength;
}

export function useTargetValueDatasource(
  config: UseTargetValueDatasourceConfig
) {
  const {
    targetType,
    targetTypeLookup,
    serviceFromDate,
    serviceToDate,
    auditGroupId,
    dispatch,
    selectedCodeList,
    eaAuditId,
    eaCaseId,
    isManualCase,
    cachedDataSource,
    setLocalDataSource,
    codeValueInstance,
    existingEntityId,
    setPropertiesDataType,
    propertiesDataKey,
    dataSourceKey,
    loadOnOpen,
    showLoader: showLoaderOption,
  } = config;

  const shouldShowLoader = showLoaderOption !== false;

  const selectedCodeListRef = useRef(selectedCodeList);
  selectedCodeListRef.current = selectedCodeList;

  const cachedDataSourceRef = useRef(cachedDataSource);
  cachedDataSourceRef.current = cachedDataSource;

  return useMemo(
    () => ({
      store: new CustomStore({
        key: "code",
        load: (loadOptions) => {
          return new Promise((resolve) => {
            const codeType = targetTypeLookup.find(
              (x) => x.value === targetType
            )?.codeType;

            if (codeType && meetsSearchThreshold(codeType, loadOptions.searchValue)) {
              if (shouldShowLoader) {
                dispatch({
                  type: SHOW_LOADER,
                  data: { isDisableFocusState: true },
                });
              }
              const params: any = {
                CodeType: codeType,
                CodeSearchStr: loadOptions.searchValue,
                SelectedCodeList: selectedCodeListRef.current,
                fromServiceDate: new Date(serviceFromDate ?? new Date()),
                toServiceDate: new Date(serviceToDate ?? new Date()),
                AuditGroupId: auditGroupId ?? 0,
              };
              if (eaAuditId !== undefined) {
                params.EaAuditId = eaAuditId;
              }
              if (eaCaseId !== undefined) {
                params.EaCaseId = eaCaseId;
              }
              if (isManualCase !== undefined) {
                params.isManualCase = isManualCase;
              }
              callOperation(GET_EA_TARGET_VALUE_SOURCES, params).then(
                (result) => {
                  if (shouldShowLoader) {
                    dispatch({
                      type: HIDE_LOADER,
                      data: { isDisableFocusState: false },
                    });
                  }
                  if (setLocalDataSource) {
                    setLocalDataSource(result);
                  }
                  resolve({
                    data: result,
                    totalCount: result?.length,
                  });
                }
              );
            } else if (
              codeType &&
              (!loadOptions.searchValue ||
                Object.keys(loadOptions).some((x) =>
                  ["filter", "customQueryParams", "expand", "select"].includes(x)
                ))
            ) {
              if (
                !["OTHER", "PT"].includes(codeType) &&
                (existingEntityId || loadOnOpen)
              ) {
                if (shouldShowLoader) {
                  dispatch({
                    type: SHOW_LOADER,
                    data: { isDisableFocusState: true },
                  });
                }
                if (cachedDataSourceRef.current && cachedDataSourceRef.current.length > 0) {
                  handleTargetValueDataSource({
                    dataSource: cachedDataSourceRef.current,
                    dispatch,
                    hideLoaderType: HIDE_LOADER,
                    setDataSource: setLocalDataSource,
                    resolve,
                    setPropertiesDataType,
                    propertiesDataKey,
                    dataSourceKey,
                  });
                  return;
                } else {
                  if (
                    loadOptions.filter?.length === 1 &&
                    loadOptions.filter[0].includes("contains")
                  ) {
                    loadOptions.searchValue = loadOptions.filter[0].pop();
                    const result = codeValueInstance
                      ?.getDataSource()
                      .items();
                    resolve({
                      data: result,
                      totalCount: result?.length,
                    });
                    if (shouldShowLoader) {
                      dispatch({
                        type: HIDE_LOADER,
                        data: { isDisableFocusState: true },
                      });
                    }
                    return;
                  }
                  const params: any = {
                    CodeType: codeType,
                    fromServiceDate: new Date(serviceFromDate ?? new Date()),
                    toServiceDate: new Date(serviceToDate ?? new Date()),
                    CodeSearchStr: loadOptions.searchValue,
                    AuditGroupId: auditGroupId ?? 0,
                    SelectedCodeList: selectedCodeListRef.current,
                  };
                  if (eaAuditId !== undefined) {
                    params.EaAuditId = eaAuditId;
                  }
                  if (eaCaseId !== undefined) {
                    params.EaCaseId = eaCaseId;
                  }
                  if (isManualCase !== undefined) {
                    params.isManualCase = isManualCase;
                  }
                  callOperation(GET_EA_TARGET_VALUE_SOURCES, params).then(
                    (result) => {
                      if (shouldShowLoader) {
                        dispatch({
                          type: HIDE_LOADER,
                          data: { isDisableFocusState: false },
                        });
                      }
                      if (setLocalDataSource) {
                        setLocalDataSource(result);
                      }
                      resolve({
                        data: result,
                        totalCount: result?.length,
                      });
                    }
                  );
                }
              } else {
                resolve({
                  data: [],
                  totalCount: 0,
                });
              }
            } else {
              resolve({
                data: [],
                totalCount: 0,
              });
            }
          });
        },
        byKey: (key) => {
          return new Promise((resolve, reject) => {
            const codeType = targetTypeLookup.find(
              (x) => x.value === targetType
            )?.codeType;

            if (!codeType || !key) {
              resolve(null);
              return;
            }

            const params: any = {
              CodeType: codeType,
              CodeList: [key],
              fromServiceDate: new Date(serviceFromDate ?? new Date()),
              toServiceDate: new Date(serviceToDate ?? new Date()),
              AuditGroupId: auditGroupId ?? 0,
            };
            if (eaAuditId !== undefined) params.EaAuditId = eaAuditId;
            if (eaCaseId !== undefined) params.EaCaseId = eaCaseId;
            if (isManualCase !== undefined) params.isManualCase = isManualCase;

            callOperation(GET_EA_TARGET_VALUE_SOURCES, params)
              .then((result) => {
                const item = Array.isArray(result)
                  ? result.find((r) => r.code === key)
                  : null;
                resolve(item ?? null);
              })
              .catch(reject);
          });
        },
      }),
    }),
    [
      targetType,
      targetTypeLookup,
      serviceFromDate,
      serviceToDate,
      auditGroupId,
      dispatch,
      eaAuditId,
      eaCaseId,
      isManualCase,
      setLocalDataSource,
      codeValueInstance,
      existingEntityId,
      setPropertiesDataType,
      propertiesDataKey,
      dataSourceKey,
      loadOnOpen,
    ]
  );
}
